import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Plus, MessageCircle, Clock, CheckCircle, AlertCircle,
  Send, Loader2, HelpCircle, TicketCheck, Search, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { downscaleImage } from '../utils/imageDownscale';

import { formatDateMonthDay } from '../utils/timeFormat';
const CATEGORIES = [
  { value: 'staffing', label: 'Staffing', color: 'bg-blue-100 text-blue-700' },
  { value: 'coordinators', label: 'Coordinators', color: 'bg-purple-100 text-purple-700' },
  { value: 'finance', label: 'Finance', color: 'bg-green-100 text-green-700' },
  { value: 'accounting', label: 'Accounting', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'technical_support', label: 'Technical Support', color: 'bg-orange-100 text-orange-700' },
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
];

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700',
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_on_user: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

function getCategoryInfo(val) {
  return CATEGORIES.find(c => c.value === val) || { label: val, color: 'bg-gray-100 text-gray-700' };
}

// ── Create Ticket Dialog ────────────────────────────────────
function CreateTicketDialog({ open, onClose, onCreated }) {
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const handleFileAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const upFile = await downscaleImage(file); // no-op for pdf/doc
    if (upFile.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploadingFile(true);
    try {
      const sigRes = await api.post('/cloudinary/signature', { folder: 'helpdesk', resource_type: 'image' });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;
      const formData = new FormData();
      formData.append('file', upFile);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'helpdesk');
      const uploadRes = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: 'POST', body: formData });
      const data = await uploadRes.json();
      if (!data.secure_url) throw new Error('Upload failed');
      setAttachments(prev => [...prev, data.secure_url]);
      toast.success('File attached');
    } catch { toast.error('Failed to upload file'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !subject || !description) return toast.error('Fill in all fields');
    setLoading(true);
    try {
      const res = await api.post('/helpdesk/tickets', { category, subject, description, priority, attachment_urls: attachments });
      toast.success(`Ticket #${res.data.ticket.ticket_number} created`);
      onCreated(res.data.ticket);
      setCategory(''); setSubject(''); setDescription(''); setPriority('normal'); setAttachments([]);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-cyan-500" /> New Support Ticket</DialogTitle>
          <DialogDescription>Fill in the details below to submit a support request.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map(c => (
                <button type="button" key={c.value} data-testid={`cat-${c.value}`}
                  onClick={() => setCategory(c.value)}
                  className={`px-3 py-2 text-xs rounded-lg border transition-all ${category === c.value ? 'border-cyan-500 bg-cyan-50 text-cyan-700 font-medium' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                >{c.label}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input id="subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of your issue" data-testid="ticket-subject" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="desc">Description</Label>
            <textarea id="desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Provide details about your issue..." rows={4} data-testid="ticket-description"
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <div className="flex gap-2">
              {['low', 'normal', 'high', 'urgent'].map(p => (
                <button type="button" key={p} onClick={() => setPriority(p)} data-testid={`priority-${p}`}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all capitalize ${priority === p ? PRIORITY_COLORS[p] + ' border-transparent font-medium' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                >{p}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Attachments</Label>
            <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-400 hover:bg-cyan-50/50 transition-colors">
              <input type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleFileAttach} disabled={uploadingFile} data-testid="ticket-attachment-input" />
              {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin text-cyan-500" /> : <Plus className="w-4 h-4 text-gray-400" />}
              <span className="text-xs text-gray-500">{uploadingFile ? 'Uploading...' : 'Add file (images, PDF, up to 10MB)'}</span>
            </label>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((url, i) => (
                  <div key={i} className="flex items-center gap-1 bg-cyan-50 text-cyan-700 text-xs px-2 py-1 rounded-full" data-testid={`attachment-${i}`}>
                    <span className="truncate max-w-[120px]">{url.split('/').pop()}</span>
                    <button type="button" onClick={() => removeAttachment(i)} className="text-cyan-500 hover:text-red-500"><AlertCircle className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="submit-ticket-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Ticket Detail View ──────────────────────────────────────
function TicketDetail({ ticketId, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [uploadingReplyFile, setUploadingReplyFile] = useState(false);

  const fetchTicket = useCallback(async () => {
    try {
      const res = await api.get(`/helpdesk/tickets/${ticketId}`);
      setTicket(res.data);
    } catch { toast.error('Failed to load ticket'); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  const handleReplyAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const upFile = await downscaleImage(file); // no-op for pdf/doc
    if (upFile.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploadingReplyFile(true);
    try {
      const sigRes = await api.post('/cloudinary/signature', { folder: 'helpdesk', resource_type: 'image' });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;
      const formData = new FormData();
      formData.append('file', upFile);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'helpdesk');
      const uploadRes = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: 'POST', body: formData });
      const data = await uploadRes.json();
      if (!data.secure_url) throw new Error('Upload failed');
      setReplyAttachments(prev => [...prev, data.secure_url]);
      toast.success('File attached');
    } catch { toast.error('Failed to upload file'); }
    finally { setUploadingReplyFile(false); e.target.value = ''; }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/helpdesk/tickets/${ticketId}/respond`, { message: reply, attachment_urls: replyAttachments });
      setReply('');
      setReplyAttachments([]);
      fetchTicket();
      toast.success('Reply sent');
    } catch { toast.error('Failed to send reply'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
  if (!ticket) return <div className="text-center py-12 text-gray-500">Ticket not found</div>;

  const cat = getCategoryInfo(ticket.category);

  return (
    <div className="space-y-4" data-testid="ticket-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700" data-testid="back-to-list">
        <ArrowLeft className="w-4 h-4" /> Back to tickets
      </button>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-gray-400">#{ticket.ticket_number}</span>
                <Badge className={cat.color + ' text-xs'}>{cat.label}</Badge>
                <Badge className={STATUS_COLORS[ticket.status] + ' text-xs'}>{ticket.status.replace('_', ' ')}</Badge>
                <Badge className={PRIORITY_COLORS[ticket.priority] + ' text-xs'}>{ticket.priority}</Badge>
              </div>
              <CardTitle className="text-lg">{ticket.subject}</CardTitle>
            </div>
          </div>
          <CardDescription className="mt-1">Created {new Date(ticket.created_at).toLocaleString()}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 whitespace-pre-wrap text-sm">{ticket.description}</p>
          {ticket.attachment_urls?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
              {ticket.attachment_urls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded hover:bg-cyan-50 hover:text-cyan-700 transition-colors" data-testid={`ticket-file-${i}`}>
                  <AlertCircle className="w-3 h-3" /> Attachment {i + 1}
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Responses */}
      <div className="space-y-3">
        {(ticket.responses || []).map(r => (
          <Card key={r.id} className={r.responder_role === 'admin' || r.responder_role === 'super_admin' ? 'border-cyan-200 bg-cyan-50/30' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-900">{r.responder_name}</span>
                {(r.responder_role === 'admin' || r.responder_role === 'super_admin') && (
                  <Badge className="bg-cyan-100 text-cyan-700 text-xs">Support</Badge>
                )}
                <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.message}</p>
              {r.attachment_urls?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {r.attachment_urls.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded hover:bg-cyan-50 hover:text-cyan-700 transition-colors">
                      <AlertCircle className="w-3 h-3" /> File {i + 1}
                    </a>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reply form */}
      {ticket.status !== 'closed' && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={sendReply} className="space-y-2">
              <div className="flex gap-2">
                <Input value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." className="flex-1" data-testid="ticket-reply-input" />
                <label className="cursor-pointer flex items-center">
                  <input type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleReplyAttach} disabled={uploadingReplyFile} data-testid="reply-attachment-input" />
                  {uploadingReplyFile ? <Loader2 className="w-4 h-4 animate-spin text-cyan-500" /> : <Plus className="w-4 h-4 text-gray-400 hover:text-cyan-500 cursor-pointer" />}
                </label>
                <Button type="submit" disabled={sending || !reply.trim()} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="send-reply-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              {replyAttachments.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {replyAttachments.map((url, i) => (
                    <div key={i} className="flex items-center gap-1 bg-cyan-50 text-cyan-700 text-xs px-2 py-0.5 rounded-full">
                      <span className="truncate max-w-[100px]">{url.split('/').pop()}</span>
                      <button type="button" onClick={() => setReplyAttachments(prev => prev.filter((_, idx) => idx !== i))} className="text-cyan-500 hover:text-red-500"><AlertCircle className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Main HelpDesk Page ──────────────────────────────────────
export default function HelpDesk() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await api.get(`/helpdesk/tickets/mine${params}`);
      setTickets(res.data);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-cyan-500" />
            <h1 className="text-base font-semibold">Help Desk</h1>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-4">
          <TicketDetail ticketId={selectedTicket} onBack={() => { setSelectedTicket(null); fetchTickets(); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="helpdesk-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-gray-500"><ArrowLeft className="w-5 h-5" /></Button>
            <HelpCircle className="w-5 h-5 text-cyan-500" />
            <h1 className="text-base font-semibold">Help Desk</h1>
          </div>
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-1" /> New Ticket
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {['all', 'open', 'in_progress', 'waiting_on_user', 'resolved', 'closed'].map(s => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition-all ${filter === s ? 'bg-cyan-500 text-white font-medium' : 'bg-white border text-gray-500 hover:border-gray-300'}`}
            >{s === 'all' ? 'All' : s.replace(/_/g, ' ')}</button>
          ))}
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <TicketCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{filter === 'all' ? 'No tickets yet' : `No ${filter.replace(/_/g, ' ')} tickets`}</p>
            <Button onClick={() => setShowCreate(true)} variant="outline" size="sm" className="mt-3" data-testid="empty-new-ticket">
              <Plus className="w-4 h-4 mr-1" /> Create your first ticket
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {tickets.map(t => {
              const cat = getCategoryInfo(t.category);
              return (
                <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow border-gray-200" onClick={() => setSelectedTicket(t.id)} data-testid={`ticket-card-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs text-gray-400">#{t.ticket_number}</span>
                          <Badge className={cat.color + ' text-xs'}>{cat.label}</Badge>
                          <Badge className={STATUS_COLORS[t.status] + ' text-xs'}>{t.status.replace(/_/g, ' ')}</Badge>
                          <Badge className={PRIORITY_COLORS[t.priority] + ' text-xs'}>{t.priority}</Badge>
                        </div>
                        <p className="font-medium text-sm text-gray-900">{t.subject}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDateMonthDay(t.created_at)}</p>
                      </div>
                      <MessageCircle className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <CreateTicketDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={(t) => setTickets(prev => [t, ...prev])} />
    </div>
  );
}
